import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentService } from '../../environment/environment.service';
import { KnownPermissionEntry, RoleDefinition, RoleDefinitionCreatePayload, TaskRoutingRow, TaskRoutingUpdatePayload, TitlePermissionOverride, TitleRoleMapping, UserMappingOption, UserPermissionOverride, UserPermissionOverridePayload } from '../models/permission-override';
import { UserInfo } from '../models/user-info';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private envService = inject(EnvironmentService);

  get userInfoApiUrl(): string {
    return `${this.envService.apiUrls.userInfo}/v1`;
  }

  public loadUserInfo(): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${this.userInfoApiUrl}/user`);
  }

  public loadUserBranches(): Observable<string[]> {
    return this.http.get<string[]>(`${this.userInfoApiUrl}/user/branches`);
  }

  public saveBranchSelections(userBranches: string[], notificationBranches: string[]): Observable<void> {
    return this.http.patch<void>(`${this.userInfoApiUrl}/user/branch-selections`, {
      user_branches: userBranches,
      notification_branches: notificationBranches,
    });
  }

  public loadUserMappings(): Observable<UserMappingOption[]> {
    return this.http.get<UserMappingOption[]>(`${this.userInfoApiUrl}/branches`);
  }

  public loadPermissionOverrideUsers(): Observable<UserMappingOption[]> {
    return this.http.get<UserMappingOption[]>(`${this.userInfoApiUrl}/permission-overrides/users`);
  }

  public loadPermissionOverrides(): Observable<UserPermissionOverride[]> {
    return this.http.get<UserPermissionOverride[]>(`${this.userInfoApiUrl}/permission-overrides`);
  }

  public loadRoleDefinitions(): Observable<RoleDefinition[]> {
    return this.http.get<RoleDefinition[]>(`${this.userInfoApiUrl}/roles`);
  }

  public createRole(payload: RoleDefinitionCreatePayload): Observable<RoleDefinition> {
    return this.http.post<RoleDefinition>(`${this.userInfoApiUrl}/roles`, payload);
  }

  public createPermissionOverride(payload: UserPermissionOverridePayload): Observable<UserPermissionOverride> {
    return this.http.post<UserPermissionOverride>(`${this.userInfoApiUrl}/permission-overrides`, payload);
  }

  public updatePermissionOverride(email: string, payload: UserPermissionOverridePayload): Observable<UserPermissionOverride> {
    return this.http.patch<UserPermissionOverride>(`${this.userInfoApiUrl}/permission-overrides/${encodeURIComponent(email)}`, payload);
  }

  public deletePermissionOverride(email: string): Observable<void> {
    return this.http.delete<void>(`${this.userInfoApiUrl}/permission-overrides/${encodeURIComponent(email)}`);
  }

  public loadKnownPermissions(): Observable<KnownPermissionEntry[]> {
    return this.http.get<KnownPermissionEntry[]>(`${this.userInfoApiUrl}/permissions`);
  }

  public updateRolePermissions(roleId: string, permissions: string[]): Observable<RoleDefinition> {
    return this.http.patch<RoleDefinition>(
      `${this.userInfoApiUrl}/roles/${encodeURIComponent(roleId)}`,
      { permissions }
    );
  }

  public loadTitleRoleMappings(): Observable<TitleRoleMapping[]> {
    return this.http.get<TitleRoleMapping[]>(`${this.userInfoApiUrl}/title-role-mappings`);
  }

  // Updates every job-code row sharing the normalized title and returns all updated rows.
  public updateTitleRoleMapping(titleNormalized: string, expectedRoleId: string): Observable<TitleRoleMapping[]> {
    return this.http.patch<TitleRoleMapping[]>(
      `${this.userInfoApiUrl}/title-role-mappings/${encodeURIComponent(titleNormalized)}`,
      { expected_role_id: expectedRoleId }
    );
  }

  public loadTitlePermissionOverrides(roleId?: string): Observable<TitlePermissionOverride[]> {
    const params = roleId ? `?role_id=${encodeURIComponent(roleId)}` : '';
    return this.http.get<TitlePermissionOverride[]>(`${this.userInfoApiUrl}/title-permission-overrides${params}`);
  }

  public batchUpsertTitlePermissionOverrides(
    items: Pick<TitlePermissionOverride, 'job_code' | 'ghr_title_normalized' | 'role_id' | 'granted_permissions' | 'restricted_permissions'>[]
  ): Observable<TitlePermissionOverride[]> {
    return this.http.patch<TitlePermissionOverride[]>(`${this.userInfoApiUrl}/title-permission-overrides`, { items });
  }

  public loadTaskRouting(): Observable<TaskRoutingRow[]> {
    return this.http.get<TaskRoutingRow[]>(`${this.userInfoApiUrl}/task-routing`);
  }

  public updateTaskRouting(taskType: string, payload: TaskRoutingUpdatePayload): Observable<TaskRoutingRow> {
    return this.http.patch<TaskRoutingRow>(
      `${this.userInfoApiUrl}/task-routing/${encodeURIComponent(taskType)}`,
      payload
    );
  }

}
